import { isArray, isString } from '@cutwise/utils';

import { dictionaryRepository } from '@/common/repositories/DictionaryRepository';
import { CustomGradeCode } from '@/customGrading/constants/customGradeCodes';
import GradeDictEntry from '@/customGrading/entity/GradeDictEntry';
import GradeResult from '@/customGrading/entity/GradeResult';
import { GradeSource, InitialGradeSource } from '@/customGrading/entity/InitialGradeSource';
import PavillionColorViboxMetric from '@/metric/entity/PavillionColorViboxMetric';
import Diamond from '@/product/entity/Diamond';

export const METRIC_KEY_BY_CODE_MAP: Partial<
  Record<CustomGradeCode, 'colorMetric' | 'pavillionColorViboxMetric' | 'deadZoneMetric'>
> = {
  [CustomGradeCode.Color]: 'colorMetric',
  [CustomGradeCode.ColorViBox2]: 'pavillionColorViboxMetric',
  [CustomGradeCode.ColorViBox3]: 'pavillionColorViboxMetric',
  [CustomGradeCode.DeadZone]: 'deadZoneMetric',
};

export function getCustomGradeMetric(product: Nullable<Diamond>, code?: Nullable<CustomGradeCode>): GradeSource {
  const metricKey = code ? METRIC_KEY_BY_CODE_MAP[code] : undefined;

  return (metricKey ? product?.[metricKey] : null) ?? null;
}

interface DictEntryMeta {
  title: string;
  shortTitle: string;
}

export type GradeSourceType = 'supplier' | 'cutwise' | 'manual';

export class CustomGradingService {
  static getDictByGradeId(gradeId: Nullable<StringOrNumber>, dict: GradeDictEntry[]) {
    return dict.find(({ id }) => id === gradeId) || null;
  }

  static getDictByGradeTitle(title: string, dict: GradeDictEntry[]) {
    return dict.find((dictEntry) => dictEntry.title.toLowerCase() === title?.toLowerCase()) || null;
  }

  static getCutwiseAutograde(code: CustomGradeCode, product: Diamond, references: Diamond[], dict: GradeDictEntry[]) {
    const metric = getCustomGradeMetric(product, code);
    const chroma = metric instanceof PavillionColorViboxMetric ? metric.chroma : undefined;

    if (chroma?.grade) {
      /*
       * for more specific grade, we need to find to which subgrade metric value belongs to
       * the logic here is next:
       *  - "metric.chroma.position" is a value between 0 and 1
       *  - first, get array of group with subgrades, e.g. E+ E E-
       *  - second, get boundaries for array according to its length, e.g. grade D have only two elements: D D-, but grade E has three elements: E+ E E-
       *  - third, create array with boundaries, e.g. [0.333333, 0.666666, 1] for E+ E E- or [0.5, 1] for D D-
       *  - fourth, get index of boundaries related to "position" value, e.g. position 0.75 in array [0.333333, 0.666666, 1] will have index 2
       *  - fifth, having the index, get grade from subgrade array, for index 2 and array E+ E E- - subgrade is E-
       * */
      const dictEntryGroup = dict.filter(({ group }) => group === chroma.grade);
      const part = 1 / dictEntryGroup.length;
      const boundaries = new Array(dictEntryGroup.length).fill(0).map((_, index) => part * (index + 1));
      const boundaryIndex = boundaries.findIndex((value) => chroma.position <= value);
      const subDict = dictEntryGroup[boundaryIndex];

      if (subDict) {
        return new InitialGradeSource(subDict, metric);
      }

      const dictEntry = dict.find(({ title }) => title === chroma.grade);

      if (dictEntry) {
        return new InitialGradeSource(dictEntry, metric);
      }
    }

    const grade = metric?.val ?? null;

    if (!grade) {
      return null;
    }

    const gradeValue = +grade;

    let referenceOnLeftSide: Nullable<Diamond> = null;
    let referenceOnRightSide: Nullable<Diamond> = null;

    for (let i = 0; i < dict.length; i++) {
      const reference = references.find((ref) => ref.id === dict[i].refId);
      const referenceValue = getCustomGradeMetric(reference ?? null, code)?.val;

      if (referenceValue) {
        const numericReferenceValue = +referenceValue;

        // If "gradeValue" is greater than current reference, this becomes "leftReference"
        if (gradeValue > numericReferenceValue) {
          referenceOnLeftSide = reference ?? null;
        } else {
          // If "gradeValue" is less than or equal to current reference, this becomes "rightReference"
          referenceOnRightSide = reference ?? null;

          break;
        }
      }
    }

    if (!referenceOnLeftSide) {
      return new InitialGradeSource(dict[0], metric);
    }

    if (!referenceOnRightSide) {
      return new InitialGradeSource(dict[dict.length - 1], metric);
    }

    const dictOnLeftSideIndex = dict.findIndex(({ refId }) => refId === referenceOnLeftSide!.id);
    const dictOnRightSideIndex = dict.findIndex(({ refId }) => refId === referenceOnRightSide!.id);

    if (dictOnLeftSideIndex === -1) {
      return new InitialGradeSource(dict[0], metric);
    }

    if (dictOnRightSideIndex === -1) {
      return new InitialGradeSource(dict[dict.length - 1], metric);
    }

    return new InitialGradeSource(dict[Math.floor((dictOnLeftSideIndex + dictOnRightSideIndex) / 2)], metric);
  }

  static getManualBracketing(code: CustomGradeCode, product: Diamond, dict: GradeDictEntry[]) {
    const gradeId = (product.customGrades?.[code] as GradeResult)?.rawValue;

    if (gradeId) {
      const grade = CustomGradingService.getDictByGradeId(gradeId, dict);

      return grade ? new InitialGradeSource(grade) : null;
    }

    return null;
  }

  static getSupplierGrade(product: Diamond, dict: GradeDictEntry[]) {
    if (product.color?.id) {
      const colorGrade = dictionaryRepository.getColorById(product.color.id);

      if (isString(colorGrade?.title)) {
        const [startGrade, endGrade] = colorGrade!.title.split('-');

        const startGradeDict = CustomGradingService.getDictByGradeTitle(startGrade, dict);

        if (startGradeDict) {
          const endGradeDict = CustomGradingService.getDictByGradeTitle(endGrade, dict);

          return new InitialGradeSource([startGradeDict, endGradeDict]);
        }
      }
    }

    return null;
  }

  static getInitialGrade(
    code: CustomGradeCode,
    product: Diamond,
    references: Diamond[],
    dict: GradeDictEntry[],
    basicDict: GradeDictEntry[],
  ) {
    // Priority 1 - check if grading diamond has "customGrades"
    const gradeByCustomGrade = CustomGradingService.getManualBracketing(code, product, dict);

    if (gradeByCustomGrade) {
      return gradeByCustomGrade;
    }

    // Priority 2 - check if grading diamond has color metric value
    const gradeByColorGrade = CustomGradingService.getSupplierGrade(product, dict);

    if (gradeByColorGrade) {
      return gradeByColorGrade;
    }

    // Priority 3 - check if grading diamond has metric value
    const gradeByMetricValue = CustomGradingService.getCutwiseAutograde(code, product, references, dict);

    if (gradeByMetricValue) {
      return gradeByMetricValue;
    }

    // Priority 4 - when no grade value found - put initial grade in the middle of the scale
    const dictMap = CustomGradingService.dictToMap(basicDict);
    const dictKeys = Object.keys(dictMap);
    const group = dictMap[dictKeys[Math.ceil(dictKeys.length / 2)]];
    const grade = group.filter((dictEntry) => !dictEntry.parent)?.[0] || group[0];

    return new InitialGradeSource(grade);
  }

  static getMetaBySourceType(type: GradeSourceType, product: Diamond): DictEntryMeta {
    if (type === 'cutwise') {
      return {
        title: 'Cutwise Autograde',
        shortTitle: 'Autograde',
      };
    }

    if (type === 'supplier') {
      if (product.certifications && product.certifications.length > 0) {
        const baseCertificate = product.certifications[0];

        if (baseCertificate?.laboratory?.title) {
          return {
            title: `${baseCertificate.laboratory.title} grade`,
            shortTitle: baseCertificate.laboratory.title,
          };
        }
      }

      return {
        title: 'Supplier Grade',
        shortTitle: 'Supplier Grade',
      };
    }

    // by default - "manual" type
    return {
      title: 'Cutwise Manual Bracketing',
      shortTitle: 'Manual Bracketing',
    };
  }

  static dictToMap(dict: GradeDictEntry[]) {
    return dict.reduce<Record<string, GradeDictEntry[]>>((result, dictEntry) => {
      const lettersOnly = dictEntry.group;

      if (isArray(result[lettersOnly])) {
        result[lettersOnly].push(dictEntry);
      } else {
        result[lettersOnly] = [dictEntry];
      }

      result[lettersOnly].sort((a, b) => a.id - b.id);

      return result;
    }, {});
  }
}
